import { Badge, Button } from "@procurement-agents/ui";
import type { QAFinding, RunViewProcurement } from "@procurement-agents/scope-api";
import { useState } from "react";
import { relativeTime } from "../labels";
import { RISK_TONE, RISKS, riskOf } from "../risk";
import { FindingsList, openFindings, type AgentApi } from "../views/DraftView";

/** The QA report as an artifact: open findings by risk (CHANGE_qa_risk) and section, closed ones under each section's disclosure,
 *  with the same "Fix this" / dismiss actions as the draft reader. */
export function QaReportReader({ run, agent, onOpenSection, onRerun }: { run: RunViewProcurement; agent?: AgentApi; onOpenSection: (sectionId: string) => void; onRerun?: () => void }) {
  const st = run.state!;
  const report = st.qa_report;
  const sections = Object.values(st.sow);
  const all = sections.flatMap((s) => s.qa_findings);
  const open = all.filter((f) => f.status === "open");
  const counts = { addressed: all.filter((f) => f.status === "addressed").length, resolved: all.filter((f) => f.status === "resolved").length, dismissed: all.filter((f) => f.status === "dismissed").length };
  const [expanded, setExpanded] = useState<string | null>(() => sections.find((s) => openFindings(s).length)?.section_id ?? null);
  const lastQa = [...st.audit_log].reverse().find((e) => e.node === "qa_review" || e.node === "qa_recheck");
  if (!report) return <p className="muted">QA has not run yet.</p>;
  return (
    <div className="qa-report">
      <header className="row between">
        <div>
          <h3>QA report · round {report.round}</h3>
          <p className="muted small">{lastQa ? `Last checked ${relativeTime(lastQa.ts)} (${lastQa.node === "qa_recheck" ? "section re-check" : "full pass"})` : ""}{run.qa_stale ? " · sections changed since the last full pass" : ""}</p>
        </div>
        <span className="row">
          {RISKS.map((r) => <Badge key={r} tone={RISK_TONE[r]}>{open.filter((f: QAFinding) => riskOf(f) === r).length} {r}</Badge>)}
          {run.qa_stale && run.status === "complete" && onRerun && <Button variant="quiet" onClick={onRerun}>Run QA again</Button>}
        </span>
      </header>
      <p className="small muted">{open.length} open · {counts.addressed} addressed · {counts.resolved} resolved · {counts.dismissed} dismissed</p>
      {open.length === 0 && <p className="good">No open findings.</p>}
      <ul className="qa-sections">
        {sections.filter((s) => s.qa_findings.length > 0).map((s) => {
          const o = openFindings(s);
          const worst = RISKS.find((r) => o.some((f) => riskOf(f) === r));
          return (
            <li key={s.section_id} className={`qa-section ${expanded === s.section_id ? "expanded" : ""}`}>
              <div className="row between">
                <button type="button" className="link section-link" onClick={() => setExpanded(expanded === s.section_id ? null : s.section_id)} aria-expanded={expanded === s.section_id}>{expanded === s.section_id ? "▾" : "▸"} {s.title}</button>
                <span className="row">
                  {worst && <Badge tone={RISK_TONE[worst]}>{o.length} open · worst {worst}</Badge>}
                  {!worst && <Badge tone="good">clear</Badge>}
                  {s.qa_findings.length - o.length > 0 && <span className="muted small">{s.qa_findings.length - o.length} closed</span>}
                  {run.rechecking?.includes(s.section_id) && <span className="chip rechecking">re-checking…</span>}
                  <button type="button" className="link small" onClick={() => onOpenSection(s.section_id)}>Open section</button>
                </span>
              </div>
              {expanded === s.section_id && <div className="qa-section-body"><FindingsList section={s} agent={agent} /></div>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
